import React, { useEffect, useMemo, useState } from "react";
import Seo from "../components/Seo";
import InquiryForm from "../components/InquiryForm";
import {
  EditorialCard, Empty, Grid, Loading, OverlayCard, PageHero, Pill, Section, SectionHead,
} from "../components/shared";
import { getList, inr } from "../lib/api";

const EXPERIENCES_CRUMBS = [{ label: "Home", to: "/" }, { label: "Experiences", to: "/experiences" }];
// Kerala backwaters houseboat deck (client photo) — the Experiences landing hero.
const EXPERIENCES_HERO = "/api/uploads/experiences/kerala-shikara.jpg";
const JOURNEYS_CRUMBS = [{ label: "Home", to: "/" }, { label: "Signature Journeys", to: "/journeys" }];
const STYLES_CRUMBS = [{ label: "Home", to: "/" }, { label: "Travel Styles", to: "/travel-styles" }];
const COLLECTIONS_CRUMBS = [{ label: "Home", to: "/" }, { label: "Collections", to: "/collections" }];
const VISA_CRUMBS = [{ label: "Home", to: "/" }, { label: "Visa Services", to: "/visa" }];
const JOURNAL_CRUMBS = [{ label: "Home", to: "/" }, { label: "Journal", to: "/blog" }];

const EXP_CATEGORIES = [
  "Delhi", "Agra", "Jaipur", "Golden Triangle", "Rajasthan", "Kerala", "Goa", "Ladakh",
  "Kashmir", "Rishikesh", "Luxury India", "Food Tours", "Walking Tours", "Shopping Tours",
  "Wildlife", "Private Chauffeur", "Airport Transfers", "Corporate Retreats",
];

export function Experiences() {
  const [items, setItems] = useState(null);
  const [cat, setCat] = useState("");

  useEffect(() => {
    getList("experiences", { limit: 120 }).then((d) => setItems(d.items || [])).catch(() => setItems([]));
  }, []);

  const filtered = useMemo(
    () => (items || []).filter((e) => !cat || (e.categories || []).includes(cat)),
    [items, cat]
  );
  if (!items) return <Loading label="Loading experiences" />;

  return (
    <>
      <Seo
        title="Private Luxury Experiences in India | Azulo Travel"
        description="Explore private experiences across India, from Kerala backwater cruises to heritage walks and wildlife. Shape your journey with Azulo Travel in New Delhi."
        image={EXPERIENCES_HERO}
        path="/experiences"
        breadcrumbs={EXPERIENCES_CRUMBS}
      />
      <PageHero
        image={EXPERIENCES_HERO}
        eyebrow="Destination Management"
        title="Curated Experiences"
        subtitle="Explore India through private heritage walks, food encounters, backwater cruises and wildlife experiences, with arrangements tailored to your party."
        height="short"
      />
      <Section testId="experiences-list">
        <div data-testid="experience-category-filter" className="mb-10 flex gap-2 overflow-x-auto pb-2">
          <Pill active={!cat} onClick={() => setCat("")} testId="exp-filter-all">
            All experiences
          </Pill>
          {EXP_CATEGORIES.map((c) => (
            <Pill
              key={c}
              active={cat === c}
              onClick={() => setCat(c)}
              testId={`exp-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {c}
            </Pill>
          ))}
        </div>
        {filtered.length === 0 ? (
          <Empty
            title="Nothing in that category yet"
            body="We are adding experiences continuously. Tell us what you have in mind and we will design it privately."
          />
        ) : (
          <Grid cols={3}>
            {filtered.map((e) => (
              <EditorialCard
                key={e.slug}
                to={`/experiences/${e.slug}`}
                image={e.card_image || e.hero_image}
                title={e.title}
                meta={`${e.city} · ${e.duration} · ${inr(e.price_inr)}`}
                subtitle={e.tagline}
                testId={`experience-card-${e.slug}`}
              />
            ))}
          </Grid>
        )}
      </Section>
      <Section wash testId="experiences-cta">
        <div className="mx-auto max-w-2xl">
          <InquiryForm
            source="experience"
            title="Want something not listed?"
            subtitle="Our India team designs private experiences to order."
          />
        </div>
      </Section>
    </>
  );
}

export function Journeys() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    getList("journeys", { limit: 60 }).then((d) => setItems(d.items || [])).catch(() => setItems([]));
  }, []);
  if (!items) return <Loading label="Loading journeys" />;
  return (
    <>
      <Seo
        title="Private Luxury Journeys from India | Azulo Travel"
        description="Explore private journeys from India: Japan in spring, Kenya safaris, Iceland, Vietnam and South Africa. Tailor the route and stays with Azulo Travel."
        image={items[0]?.hero_image}
        path="/journeys"
        breadcrumbs={JOURNEYS_CRUMBS}
      />
      <PageHero
        image={items[0]?.hero_image}
        eyebrow="Signature Journeys"
        title="Signature journeys from India"
        subtitle="Private itineraries to inspire your next journey. Choose a starting point, then shape the route, stays and pace around your interests and departure city in India."
        height="short"
      />
      <Section testId="journeys-list">
        <Grid cols={3}>
          {items.map((j) => (
            <OverlayCard
              key={j.slug}
              to={`/journeys/${j.slug}`}
              image={j.card_image || j.hero_image}
              title={j.title}
              meta={`${j.nights} nights · from ${inr(j.price_inr)}`}
              subtitle={j.tagline}
              testId={`journey-card-${j.slug}`}
            />
          ))}
        </Grid>
      </Section>
    </>
  );
}

export function TravelStyles() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    getList("travel_styles", { limit: 40 }).then((d) => setItems(d.items || [])).catch(() => setItems([]));
  }, []);
  if (!items) return <Loading label="Loading travel styles" />;
  return (
    <>
      <Seo
        title="Bespoke Travel Styles for Indian Travellers | Azulo Travel"
        description="Explore private luxury, family, honeymoon, wellness and wildlife holidays from India. Find your travel style and plan a bespoke journey with Azulo Travel."
        image={items[0]?.hero_image}
        path="/travel-styles"
        breadcrumbs={STYLES_CRUMBS}
      />
      <PageHero
        image={items[0]?.hero_image}
        eyebrow="Travel Styles"
        title="Travel, your way"
        subtitle="The same destination is a completely different holiday depending on why you are going and who you are going with."
        height="short"
      />
      <Section testId="travel-styles-list">
        <Grid cols={3}>
          {items.map((s) => (
            <EditorialCard
              key={s.slug}
              to={`/travel-styles/${s.slug}`}
              image={s.card_image || s.hero_image}
              title={s.title}
              meta={s.tagline}
              subtitle={(s.overview || "").split("\n")[0]}
              testId={`travel-style-card-${s.slug}`}
            />
          ))}
        </Grid>
      </Section>
    </>
  );
}

export function Collections() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    getList("seasonal_collections", { limit: 40 })
      .then((d) => setItems(d.items || []))
      .catch(() => setItems([]));
  }, []);
  if (!items) return <Loading label="Loading collections" />;
  return (
    <>
      <Seo
        title="Seasonal Luxury Holidays from India | Azulo Travel"
        description="Explore seasonal holidays from India: Japanese cherry blossom, Northern Lights, safari season and festive escapes. Discuss the right travel window with Azulo."
        image={items[0]?.hero_image}
        path="/collections"
        breadcrumbs={COLLECTIONS_CRUMBS}
      />
      <PageHero
        image={items[0]?.hero_image}
        eyebrow="Seasonal Collections"
        title="Timing is the whole trip"
        subtitle="Some journeys only work in a particular fortnight of the year. These are those."
        height="short"
      />
      <Section testId="collections-list">
        <Grid cols={3}>
          {items.map((c) => (
            <OverlayCard
              key={c.slug}
              to={`/collections/${c.slug}`}
              image={c.card_image || c.hero_image}
              title={c.title}
              meta={c.window}
              subtitle={(c.overview || "").split("\n")[0]}
              testId={`collection-card-${c.slug}`}
            />
          ))}
        </Grid>
      </Section>
    </>
  );
}

export function VisaList() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    getList("visa_pages", { limit: 60 }).then((d) => setItems(d.items || [])).catch(() => setItems([]));
  }, []);
  if (!items) return <Loading label="Loading visa services" />;
  return (
    <>
      <Seo
        title="Visa Guidance for Indian Travellers | Azulo Travel"
        description="Country-wise visa guidance for Indian travellers: document checklists, application steps and planning notes. Discuss current requirements with Azulo Travel."
        image={items[0]?.hero_image}
        path="/visa"
        breadcrumbs={VISA_CRUMBS}
      />
      <PageHero
        image={items[0]?.hero_image}
        eyebrow="Visa Services"
        title="Visas, filed properly"
        subtitle="Explore country-specific checklists and application guidance for Indian travellers. Requirements can change; confirm current details before applying. Approval rests with the issuing authority."
        height="short"
      />
      <Section testId="visa-list">
        <Grid cols={3}>
          {items.map((v) => (
            <div key={v.slug} data-testid={`visa-card-${v.slug}`}>
              <EditorialCard
                to={`/visa/${v.slug}`}
                image={v.hero_image}
                title={`${v.flag_emoji || ""} ${v.title}`}
                meta={`${v.processing_time} · ${v.azulo_fee}`}
                subtitle={v.visa_type}
                testId={`visa-card-link-${v.slug}`}
              />
            </div>
          ))}
        </Grid>
      </Section>
      <Section wash testId="visa-cta">
        <div className="mx-auto max-w-2xl">
          <InquiryForm
            source="visa"
            compact
            title="Need a visa filed?"
            subtitle="Tell us the country and your travel dates. We will send the checklist within one business day."
          />
        </div>
      </Section>
    </>
  );
}

export function Blog() {
  const [items, setItems] = useState(null);
  const [cat, setCat] = useState("");
  const CATS = ["Travel Guides", "Destination Guides", "Visa Updates", "Luxury Travel",
    "Corporate Travel", "Travel Tips"];

  useEffect(() => {
    getList("blogs", { limit: 80 }).then((d) => setItems(d.items || [])).catch(() => setItems([]));
  }, []);

  const filtered = useMemo(
    () => (items || []).filter((b) => !cat || b.category === cat),
    [items, cat]
  );
  if (!items) return <Loading label="Loading journal" />;

  return (
    <>
      <Seo
        title="Luxury Travel Guides for Indian Travellers | Azulo Journal"
        description="Destination guides, seasonal planning and practical travel advice for Indian travellers. Explore private journey ideas in the Azulo Travel journal."
        image={items[0]?.hero_image}
        path="/blog"
        breadcrumbs={JOURNAL_CRUMBS}
      />
      <PageHero
        image={items[0]?.hero_image}
        eyebrow="The Journal"
        title="Written by the people who design the journeys"
        subtitle="Destination guides, seasonal timing and practical advice for planning considered journeys from India."
        height="short"
      />
      <Section testId="blog-list">
        <div data-testid="blog-category-filter" className="mb-10 flex gap-2 overflow-x-auto pb-2">
          <Pill active={!cat} onClick={() => setCat("")} testId="blog-filter-all">
            All articles
          </Pill>
          {CATS.map((c) => (
            <Pill
              key={c}
              active={cat === c}
              onClick={() => setCat(c)}
              testId={`blog-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {c}
            </Pill>
          ))}
        </div>
        {filtered.length === 0 ? (
          <Empty title="No articles in that category yet" />
        ) : (
          <Grid cols={3}>
            {filtered.map((b) => (
              <EditorialCard
                key={b.slug}
                to={`/blog/${b.slug}`}
                image={b.hero_image}
                title={b.title}
                meta={`${b.category} · ${b.read_time}`}
                subtitle={b.excerpt}
                testId={`blog-card-${b.slug}`}
              />
            ))}
          </Grid>
        )}
      </Section>
    </>
  );
}
